import type { CsvImportModeLabel } from './utils/csv-import';
import { getCsvBoxEnvironment } from './config';

export interface CallBackData {
  custom_fields: {
    user_id: string;
    instruction_mode?: CsvImportModeLabel;
    /** Flow legacy seulement (marques, ou Alt + clic pour les internes). */
    anchor_key?: 'auto' | 'url' | 'sku';
    deal_id?: string;
    /** Token Auth0 de la sale, vérifié par le webhook (cf. `buildCsvBoxUser`). */
    auth_token?: string;
  };
  destination_type: string;
  import_id: number;
  import_status: string;
  /** URL of the original uploaded file. Absent unless the CSVBox sheet exposes it. */
  raw_file?: string;
  row_count: number;
  row_fail: number;
  row_success: number;
  sheet_id: number;
  sheet_name: string;
  original_filename: string;
  ssv_row_fail: number;
  external_validations_failed_requests: number;
  ssv_table_error: number;
  if_table_error: number;
  ssv_fail: number;
  ev_failed: number;
  ssv_enabled: number;
  external_validation: number;
  import_starttime: number;
  import_endtime: number;
  env_name: string;
  import_description?: string;
  column_mappings: Array<Record<string, string>>;
  /** Present when "Send JSON data to client" is enabled in CSVBox sheet Destination. Avoids CORS (no fetch of raw_file). */
  rows?: Record<string, unknown>[];
  raw_columns?: string[];
  ignored_columns?: string[];
}

type Callback = (result: boolean, data: CallBackData) => void;

/**
 * Attributs posés par `setUser`. CSVBox les renvoie dans `custom_fields`, au
 * callback client comme au webhook backend, à condition qu'ils soient posés
 * AVANT l'ouverture de la modale.
 */
export interface CsvBoxUser {
  user_id: string;
  instruction_mode: string;
  /** Flow legacy seulement (marques, ou Alt + clic pour les internes). */
  anchor_key?: string;
  deal_id?: string;
  /**
   * Token Auth0 de la sale, vérifié par le webhook pour identifier l'auteur de
   * l'import : `user_id` vient du navigateur et peut être falsifié.
   */
  auth_token?: string;
}

/**
 * Budget CSVBox : `setUser` ne transmet que `user_id` + 4 attributs, les
 * attributs en trop sont perdus sans erreur. Tuple d'au plus 4 éléments.
 */
export type CsvBoxAttributeBudget<Key> = readonly [Key?, Key?, Key?, Key?];

type CsvBoxUserAttributeKey = Exclude<keyof CsvBoxUser, 'user_id'>;

/**
 * Attributs transmis par `buildCsvBoxUser`, vérifiés à la compilation : plus de
 * 4 clés ne satisfont pas le budget. Le flux legacy les occupe toutes
 * (`anchor_key` en plus) : un nouvel attribut exige d'en libérer un (ex.
 * `anchor_key`, déprécié).
 */
const CSV_BOX_USER_ATTRIBUTE_KEYS = [
  'instruction_mode',
  'anchor_key',
  'deal_id',
  'auth_token',
] as const satisfies CsvBoxAttributeBudget<CsvBoxUserAttributeKey>;

/**
 * `never` dès qu'un attribut de `CsvBoxUser` n'est pas listé dans
 * `CSV_BOX_USER_ATTRIBUTE_KEYS` : ajouter un attribut sans l'inscrire au budget
 * casse la compilation des appels à `buildCsvBoxUser`.
 */
type BudgetedCsvBoxUser = [
  Exclude<CsvBoxUserAttributeKey, (typeof CSV_BOX_USER_ATTRIBUTE_KEYS)[number]>,
] extends [never]
  ? CsvBoxUser
  : never;

/**
 * Construit les attributs `setUser`, sans les attributs absents (ils compteraient
 * dans la limite CSVBox sans rien porter).
 */
export function buildCsvBoxUser(user: BudgetedCsvBoxUser): CsvBoxUser {
  const attributes = Object.fromEntries(
    CSV_BOX_USER_ATTRIBUTE_KEYS.flatMap((key) => {
      const value = user[key];
      return value === undefined || value === '' ? [] : [[key, value]];
    })
  ) as Omit<CsvBoxUser, 'user_id'>;

  return { user_id: user.user_id, ...attributes };
}

/** Colonne dynamique CSVBox (cf. help.csvbox.io/advanced-installation/dynamic-columns). */
export interface DynamicColumn {
  column_name: string;
  display_label?: string;
  info_hint?: string;
  matching_keywords?: string;
  /** CSVBox compatibility fallback when system keyword matching is disabled. */
  keywords?: Array<{ value: string }>;
  type?: string;
  required?: boolean;
  validators?: Record<string, unknown>;
}

/**
 * Licences CSVBox (sheet Smart Offer produits physiques).
 * `current` est la licence active ; `deprecated` est conservée pour référence.
 */
const CSV_BOX_LICENSES = {
  current: '91fZyEMTeshDfmDVI5L5Vhv1IHhxKm',
  /** @deprecated Remplacée par `current`. */
  deprecated: 'CKUJY4cwdL5wp1UNIohKL8XhBi78sH',
} as const;

const CSV_BOX_PHYSICAL_PRODUCT_TEMPLATE = CSV_BOX_LICENSES.current;

declare global {
  class CSVBoxImporter {
    public readonly setOptions: (options: Record<string, unknown>) => void;
    public readonly setUser: (user: CsvBoxUser) => void;
    public readonly setDynamicColumns: (columns: DynamicColumn[]) => void;
    public readonly openModal: () => void;
    public readonly listen: (
      event: 'onReady' | 'onSubmit' | 'onClose',
      callback: (data?: unknown) => void
    ) => void;

    constructor(
      apiKey: string,
      options: Record<string, unknown>,
      callback: (result: boolean, data: CallBackData) => void,
      settings?: Record<string, unknown>
    );
  }
  interface Window {
    CSVBoxImporter: CSVBoxImporter;
  }
}

type CloseCallback = () => void;

export class CsvBox {
  private importer: CSVBoxImporter | null = null;
  private subscribers = new Set<Callback>();
  private closeSubscribers = new Set<CloseCallback>();
  private readonly importerCallback: Callback = (result, data) => {
    for (const subscriber of this.subscribers) {
      subscriber(result, data);
    }
  };
  constructor() {}

  private createImporter() {
    this.importer = new CSVBoxImporter(
      CSV_BOX_PHYSICAL_PRODUCT_TEMPLATE,
      {},
      this.importerCallback,
      { environment: getCsvBoxEnvironment() }
    );
    // Fires whenever the modal is dismissed (after a completed import or
    // when the user simply leaves) — dispatched to onClose subscribers.
    this.importer.listen('onClose', () => {
      for (const subscriber of this.closeSubscribers) {
        subscriber();
      }
    });
  }

  private ensureImporter() {
    if (!this.importer) {
      this.createImporter();
    }
  }

  /**
   * Recrée l'importer pour repartir d'un état propre. CSVBox conserve les
   * colonnes dynamiques déjà fournies sur une même instance : sans ce reset,
   * changer de mode de calcul entre deux ouvertures accumule les colonnes prix
   * (ex. « Prix de rétrocession ht » qui réapparaît, vide et required, en mode
   * Marge fixe). À appeler en tout début d'ouverture, AVANT setOptions/setUser/
   * setDynamicColumns. Les abonnements (subscribers/closeSubscribers) vivent sur
   * CsvBox, pas sur l'importer, donc ils sont préservés.
   */
  public reset() {
    this.createImporter();
  }

  openModal() {
    this.ensureImporter();
    this.importer!.openModal();
  }

  subscribe(callback: Callback) {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  public setOptions(options: { language: 'fr' | 'en' }) {
    this.ensureImporter();
    this.importer!.setOptions({
      language: options.language,
    });
  }

  public setUser(user: CsvBoxUser) {
    this.ensureImporter();
    this.importer!.setUser(user);
  }

  public setDynamicColumns(columns: DynamicColumn[]) {
    this.ensureImporter();
    this.importer!.setDynamicColumns(columns);
  }

  subscribeOnce(callback: Callback) {
    const onceWrapper: Callback = (result, data) => {
      this.subscribers.delete(onceWrapper);
      callback(result, data);
    };
    this.subscribers.add(onceWrapper);
    return () => {
      this.subscribers.delete(onceWrapper);
    };
  }

  subscribeOnceClose(callback: CloseCallback) {
    this.ensureImporter();
    const onceWrapper: CloseCallback = () => {
      this.closeSubscribers.delete(onceWrapper);
      callback();
    };
    this.closeSubscribers.add(onceWrapper);
    return () => {
      this.closeSubscribers.delete(onceWrapper);
    };
  }
}

export const csvBoxSingleton = new CsvBox();
